// Team logos are displayed at 375x154, so ~1000px on the longest side keeps
// them crisp on high-DPI screens while avoiding multi-megabyte uploads.
const MAX_LOGO_DIMENSION = 1000;
// Only applied to lossy formats (jpeg/webp); ignored for png. Kept high so the
// logo is downscaled, not visibly degraded.
const LOSSY_QUALITY = 0.9;

// Downscale a base64 data-URL image to fit within `maxDimension` on its longest
// side, preserving the source format. Returns a Promise of a data URL. Non-image
// input, already-small images, and decode errors resolve to the original value
// unchanged so a resize never blocks a save.
export function resizeImageDataUrl(dataUrl, maxDimension = MAX_LOGO_DIMENSION) {
  return new Promise((resolve) => {
    if (typeof dataUrl !== 'string' || dataUrl.indexOf('data:image/') !== 0) {
      resolve(dataUrl);
      return;
    }

    const mimeMatch = /^data:([^;,]+)[;,]/.exec(dataUrl);
    const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';

    const img = new Image();

    img.onload = () => {
      try {
        const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));

        // Already within bounds — return untouched, no needless re-encode.
        if (scale >= 1) {
          resolve(dataUrl);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);

        const isLossy = mimeType === 'image/jpeg' || mimeType === 'image/webp';
        resolve(isLossy ? canvas.toDataURL(mimeType, LOSSY_QUALITY) : canvas.toDataURL(mimeType));
      } catch (e) {
        resolve(dataUrl);
      }
    };

    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}
